/**
 * @file CategoryTreeList.tsx
 * @description Дерево категорий с перетаскиванием (phase14.0.2; phase19.31 —
 * образец (б): шапка колонок с 1024, строки `CategoryTreeRow`).
 *
 * Устройство перетаскивания (исправление зависания phase14.0.2, не менять):
 *  - ОДИН `DndContext` на всё дерево, датчики создаются один раз;
 *  - на каждый уровень (один родитель) — свой `SortableContext`: категорию
 *    можно переставить только среди соседей;
 *  - системные корни не перетаскиваются.
 */

import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";

import { CATEGORY_GRID, CategoryTreeRow } from "./CategoryTreeRow";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import type { RowAction } from "../../ui/RowActionsMenu";
import type { CatalogCategory } from "../../../lib/api-catalog";
import type { ProductKind } from "../../../lib/api-product-kinds";
import type { CategoryTreeNode } from "../../../lib/catalog-category-tree";
import { cn } from "@/lib/utils";

interface Props {
  tree: CategoryTreeNode[];
  expanded: Set<string>;
  onToggle: (id: string) => void;
  byId: Map<string, CatalogCategory>;
  kindsById: Map<string, ProductKind>;
  rowActions: (category: CatalogCategory) => RowAction[];
  onReorder: (activeId: string, overId: string) => void;
}

export function CategoryTreeList({ tree, expanded, onToggle, byId, kindsById, rowActions, onReorder }: Props) {
  // Датчики — один раз на компонент: иначе бесконечная перерисовка.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd({ active, over }: DragEndEvent) {
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  }

  const branch = (nodes: CategoryTreeNode[]) => (
    <SortableContext items={nodes.filter((n) => !n.isSystemRoot).map((n) => n.id)} strategy={verticalListSortingStrategy}>
      {nodes.map((node) => (
        <div key={node.id} role="treeitem" aria-expanded={node.children.length > 0 ? expanded.has(node.id) : undefined} aria-selected={false}>
          <CategoryTreeRow
            node={node}
            open={expanded.has(node.id)}
            onToggle={() => onToggle(node.id)}
            byId={byId}
            kindsById={kindsById}
            actions={rowActions(node)}
          />
          {node.children.length > 0 && expanded.has(node.id) ? <div role="group">{branch(node.children)}</div> : null}
        </div>
      ))}
    </SortableContext>
  );

  return (
    <div className={LIST_FRAME_CLASS}>
      {/* Шапка колонок — с 1024; отступ слева = ручка + стрелка (2 × 32). */}
      <div className="hidden min-h-11 items-center border-b border-[var(--border)] bg-surface-low pl-[68px] pr-14 lg:flex">
        <div className={cn("type-caption min-w-0 flex-1 font-medium text-on-surface-variant", CATEGORY_GRID)}>
          <span>Категория</span>
          <span>Вид товара</span>
          <span className="text-right" title="Вместе с подкатегориями">Товаров</span>
          <span>На сайте</span>
        </div>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <div role="tree" aria-label="Дерево категорий">
          {branch(tree)}
        </div>
      </DndContext>
    </div>
  );
}
